import type React from 'react';
import { createContext, useContext, useMemo } from 'react';

import { breakpoints } from '@coursera/cds-common';

import { useMediaQuery } from '@core/utils';

export type CardSize = 'standard' | 'compact';

export type CardContextValue = {
  size?: CardSize;
  isXs?: boolean;
  variant?: 'grid' | 'list';
  isCompactStyle?: boolean;
};

/**
 * Utility function to determine if compact styling should be applied.
 */
export const getIsCompactStyle = (
  variant?: 'grid' | 'list',
  size?: CardSize,
  isXs?: boolean
): boolean => {
  return variant === 'list'
    ? size === 'compact'
    : Boolean(isXs && size === 'compact');
};

export const CardContext = createContext<CardContextValue | undefined>(
  undefined
);

export const useCardContext = () => {
  return useContext(CardContext);
};

type CardContextProviderProps = {
  size?: CardSize;
  variant?: 'grid' | 'list';
  children: React.ReactNode;
};

export const CardContextProvider = ({
  size,
  variant,
  children,
}: CardContextProviderProps) => {
  const isXs = useMediaQuery(breakpoints.down('xs'));

  const value = useMemo(
    () => ({
      size,
      isXs,
      variant,
      isCompactStyle: getIsCompactStyle(variant, size, isXs),
    }),
    [size, isXs, variant]
  );

  return <CardContext.Provider value={value}>{children}</CardContext.Provider>;
};
